import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, X, Clock } from "lucide-react";
import { toast } from "sonner";

const STAGE_LABEL = { l1: "L1", l2: "L2" };

export default function ApprovalBanner({ item }) {
  const qc = useQueryClient();
  const { me } = useApp();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState("");

  const { data: config } = useQuery({ queryKey: ["approval-config", item.project_id], queryFn: () => get(`/pm/projects/${item.project_id}/approval-config`) });
  const { data: approvals = [] } = useQuery({ queryKey: ["approvals", item.id], queryFn: () => get(`/pm/items/${item.id}/approvals`) });

  const decide = useMutation({
    mutationFn: ({ approvalId, decision }) => post(`/pm/items/${item.id}/approvals/${approvalId}/decide`, { decision, comments: comment || null, _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["item", item.id] }); qc.invalidateQueries({ queryKey: ["approvals", item.id] }); toast.success("Decision recorded"); setRejecting(false); setComment(""); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not record decision"),
  });

  if (!config || !config.applies_to_types?.includes(item.type)) return null;

  const pending = approvals.find((a) => a.status === "pending");
  const requiredRole = pending ? config[`${pending.stage}_approver_role`] : null;
  const canDecide = pending && me?.role_id === requiredRole;

  const stageStatus = (stage) => {
    const rows = approvals.filter((a) => a.stage === stage);
    if (!rows.length) return "not started";
    const latest = rows[rows.length - 1];
    return latest.status;
  };

  if (!item.approval_status && !approvals.length) return null;

  return (
    <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
      <div className="flex items-center gap-4">
        {["l1", "l2"].map((stage) => {
          const status = stageStatus(stage);
          return (
            <div key={stage} className="flex items-center gap-1.5">
              {status === "approved" ? <Check className="h-4 w-4 text-emerald-600" /> : status === "rejected" ? <X className="h-4 w-4 text-red-600" /> : <Clock className="h-4 w-4 text-amber-600" />}
              <span className="text-sm font-medium">{STAGE_LABEL[stage]}</span>
              <StatusBadge family={status === "approved" ? "green" : status === "rejected" ? "red" : status === "pending" ? "amber" : "slate"} label={status} />
            </div>
          );
        })}
        {canDecide && !rejecting && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" className="text-red-600" data-testid="approval-reject-btn" onClick={() => setRejecting(true)}>Reject</Button>
            <Button size="sm" data-testid="approval-approve-btn" onClick={() => decide.mutate({ approvalId: pending.id, decision: "approve" })}>
              Approve ({STAGE_LABEL[pending.stage]})
            </Button>
          </div>
        )}
      </div>
      {canDecide && rejecting && (
        <div className="mt-3 flex items-center gap-2">
          <Input data-testid="approval-reject-comment" placeholder="Reason for rejection" className="h-8 flex-1 text-xs" value={comment} onChange={(e) => setComment(e.target.value)} />
          <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>Cancel</Button>
          <Button size="sm" variant="destructive" data-testid="approval-reject-confirm" onClick={() => decide.mutate({ approvalId: pending.id, decision: "reject" })}>Confirm Reject</Button>
        </div>
      )}
      {approvals.some((a) => a.comments) && (
        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
          {approvals.filter((a) => a.comments).map((a) => <div key={a.id}>{STAGE_LABEL[a.stage]} · {a.approver_name}: {a.comments}</div>)}
        </div>
      )}
    </div>
  );
}
